import {
  ItemTypes,
  MASKS_TYPE,
  minContainerHeight,
  uuid,
} from '../constants/constants';
import {
  masksDefaultHeight,
  pageHeight,
  pageWidth,
} from '../constants/gridConfig';
import {
  isASelectionBox,
  mutateRemoveBox,
  mutateSelectContainer,
} from './boxes';
import { mutateAddColumns, mutateDeleteColumns, newColumn } from './columns';
import { removeSectionTmpConfiguration } from './styles';

export const newContainer = ({
  height = 'auto',
  columnsIds = [],
  editableConfiguration,
  KeyPdfToImage = null,
}) => {
  return {
    id: uuid(),
    columnsIds,
    height,
    editable: editableConfiguration,
    ...(KeyPdfToImage && { KeyPdfToImage }),
  };
};

export const mutateAddContainers = ({
  draft,
  index = draft.containers.length,
  count = 1,
  columnsSizes = [1],
  drawMode = false,
  box,
  from = null,
  KeyPdfToImage = null,
  fromCopiedContainer = false,
  shouldSelect = true,
}) => {
  const boxes = !box
    ? [] // Si `box` est null ou undefined, retourner un tableau vide
    : Array.isArray(box)
    ? box // Si `box` est déjà un tableau, l'utiliser directement
    : box.type === ItemTypes.DRAW
    ? [] // Si `box.type` est `ItemTypes.DRAW`, retourner un tableau vide
    : [box]; // Sinon, envelopper `box` dans un tableau

  const containerHeight = computeContainerHeight({
    drawMode,
    landscape: draft.landscape,
    boxType: box?.type,
    from,
    header: draft.masks.find((mask) => mask?.id === MASKS_TYPE.HEADER.id),
    footer: draft.masks.find((mask) => mask?.id === MASKS_TYPE.FOOTER.id),
  });
  const containersToInsert = Array(count)
    .fill()
    .map(() =>
      newContainer({
        height: fromCopiedContainer?.container_height ?? containerHeight,
        editableConfiguration:
          draft.currentProposal?.id !== null
            ? true
            : draft.configuration.editableTemplate,
        KeyPdfToImage,
      })
    );
  containersToInsert.forEach((container, i) => {
    const columns = columnsSizes.map((size) =>
      newColumn({
        size,
        drawMode,
        maskId: fromCopiedContainer?.maskId,
      })
    );
    const columnsIds = columns.map((c) => c.id);

    container.columnsIds = columnsIds;
    if (index !== null) {
      draft.containers.splice(index + i + 1, 0, container);
    } else {
      draft.containers.unshift(container);
    }
    if (fromCopiedContainer) {
      if (drawMode) mutateAddColumns(draft, columns, boxes);
      else {
        columns.forEach((column, i) => {
          mutateAddColumns(draft, [column], [boxes[i]]);
        });
      }
    } else {
      mutateAddColumns(draft, columns, boxes);
    }
  });
  if (shouldSelect && containersToInsert[0]) {
    mutateDropInContainer({ draft, container: containersToInsert[0], box });
  }
};

export const mutateRemoveContainer = (draft, containerId) => {
  let columnsToDeleteIds = [];
  // Collect the linked container id (chapter sep ↔ linked title pair) before filtering
  const linkedContainerIds = new Set();
  draft.containers.forEach((c) => {
    if (c.id === containerId && c.linkedContainerId) {
      linkedContainerIds.add(c.linkedContainerId);
    }
  });
  draft.containers = draft.containers.filter((c) => {
    if (c.id === containerId || linkedContainerIds.has(c.id)) {
      columnsToDeleteIds.push(...c.columnsIds);
      return false;
    }
    return true;
  });
  mutateDeleteColumns(draft, columnsToDeleteIds);
};

export const mutateRemoveContainers = (draft, containerIds = []) => {
  let columnsToDeleteIds = [];
  // Expand containerIds to include any linked containers
  const idsToRemove = new Set(containerIds);
  draft.containers.forEach((c) => {
    if (idsToRemove.has(c.id) && c.linkedContainerId) {
      idsToRemove.add(c.linkedContainerId);
    }
  });
  draft.containers = draft.containers.filter((c) => {
    if (idsToRemove.has(c.id)) {
      columnsToDeleteIds.push(c.columnsIds);
      return false;
    }
    return true;
  });
  mutateDeleteColumns(draft, columnsToDeleteIds.flat());
};

export const mutateRemmoveSectionContainers = (
  draft,
  fromBoxId,
  { sectionId, dateTimestamp }
) => {
  let columnsToDeleteIds = [];
  const findBoxeByColumnId = (columnId) =>
    draft.boxes.find((b) => b.columnId === columnId);
  let sectionTmpId = [];
  draft.containers = draft.containers.filter((c) => {
    const box = findBoxeByColumnId(c.columnsIds[0]);
    const shouldDelete =
      (fromBoxId && c.fromBoxId === fromBoxId) ||
      (sectionId &&
        dateTimestamp &&
        c.sectionId === sectionId &&
        c.dateTimestamp === dateTimestamp) ||
      (box?.id === fromBoxId && box?.type === ItemTypes.SECTION_LINK);
    if (shouldDelete) {
      columnsToDeleteIds.push(...c.columnsIds);
      if (
        box?.from_linked_section &&
        (box?.tableStyleId ||
          box?.textStyleId ||
          box?.checkboxStyleId ||
          box?.selectorStyleId)
      )
        sectionTmpId.push(
          box?.tableStyleId ||
            box?.textStyleId ||
            box?.checkboxStyleId ||
            box?.selectorStyleId
        );
      return false;
    }
    return true;
  });
  removeSectionTmpConfiguration({
    sectionTmpId: [...new Set(sectionTmpId)],
    draft,
  });

  mutateDeleteColumns(draft, columnsToDeleteIds);
  const boxesFormToDelete = draft.boxes.filter(
    (b) =>
      (fromBoxId && b.fromBoxId === fromBoxId) ||
      b.id === b.fromBoxId ||
      (sectionId &&
        dateTimestamp &&
        b.fromSectionId === sectionId &&
        b.dateTimestamp === dateTimestamp)
  );
  for (const box of boxesFormToDelete) {
    mutateRemoveBox(draft, box.id);
  }
};

export const mutateUpdateContainer = (draft, updatedContainer) => {
  draft.containers = draft.containers.map((c) => {
    if (c.id === updatedContainer.id) {
      return updatedContainer;
    }
    return c;
  });
  // selectedContainer est un instantané pris à la sélection : sans ce
  // rafraîchissement, un réglage fait depuis le panneau (container sélectionné)
  // n'y paraissait pas, et copier le container reprenait l'ancienne valeur.
  if (draft.selectedContainer?.id === updatedContainer.id)
    draft.selectedContainer = updatedContainer;
};

export const computeContainerHeight = ({
  drawMode,
  landscape,
  boxType,
  from = null,
  header,
  footer,
}) => {
  let containerHeight = 'auto';
  if (drawMode) {
    if (from === 'PdfToImageSizeMaxDefault') {
      containerHeight = maxContainerHeight({ header, footer });
    } else if (landscape && (!boxType || !isASelectionBox(boxType))) {
      containerHeight = pageWidth;
    } else if (boxType === ItemTypes.SECTION_VARIABLE) {
      containerHeight = minContainerHeight + 32;
    } else {
      containerHeight = minContainerHeight;
    }
  }
  return containerHeight;
};

export const mutateDropInContainer = ({ draft, container, box }) => {
  mutateSelectContainer(draft, container);
  draft.selectedContainerIds = container?.id ? [container.id] : [];
  draft.activeContainerId = container?.id || null;
  if (box) {
    // ItemTypes.DRAW n'est jamais réellement inséré dans draft.boxes à ce
    // stade (cf. mutateAddContainers et dropBoxInColumn) : sélectionner son id
    // pointerait vers une box inexistante. On efface plutôt l'ancienne
    // sélection pour ne pas laisser le panel d'une autre box affiché.
    if (box.type === ItemTypes.DRAW) {
      draft.selectedBoxId = null;
      draft.groupSelection = [];
      draft.boxVariableSelectedId = null;
    } else {
      draft.selectedBoxId = box.id;
    }
  } else {
    // Container posé sans box (grille, disposition vide, container collé ou
    // dupliqué vide) : la box sélectionnée avant garderait la barre de droite
    // sur son panel, celui de la nouvelle grille ne s'affichant que sans box
    // sélectionnée (cf. layoutContainerId dans RightSide).
    draft.selectedBoxId = null;
    draft.groupSelection = [];
    draft.boxVariableSelectedId = null;
  }
};

/**
 * Ids sous lesquels un container peut apparaître dans les pages d'aperçu.
 *
 * Le service PDF aplatit les lignes principales d'une colonne SEULE dans son
 * container : chacune devient un container frère, et l'aperçu renvoie alors
 * l'id de chaque ligne, jamais celui du container d'origine (cf.
 * flattenMainRows dans pdf/pdfGen/initializeEditor.js — même condition des deux
 * côtés : une seule colonne, au moins deux lignes).
 */
export const getContainerPrintIds = (container, columns) => {
  if (!container?.id) return [];
  if (container.columnsIds?.length !== 1) return [container.id];
  const rowIds =
    columns?.find((c) => c.id === container.columnsIds[0])?.rowContainerIds ??
    [];
  if (rowIds.length < 2) return [container.id];
  return [container.id, ...rowIds];
};

export const maxContainerHeight = ({ header, footer }) => {
  return (
    pageHeight -
    (header?.height ?? masksDefaultHeight.header) -
    (footer?.height ?? masksDefaultHeight.footer)
  );
};

/**
 * Retourne les containers entre le container titre niveau 1 (<= visible) et le prochain titre niveau 1 (exclu).
 * Si aucun titre niveau 1 suivant, retourne jusqu'à la fin. Si le titre trouvé est le 1er du document,
 * inclut aussi tous les containers avant lui.
 * @param {Object} params - Les paramètres de la fonction
 * @param {Array} params.containers - La liste des containers
 * @param {Array} params.boxes - La liste des boxes
 * @param {Array} params.columns - La liste des colonnes
 * @param {string|null} params.startContainerId - L'ID du container de départ
 * @returns {Object} - Objet contenant containersIncluded, boxesIncluded, columnsIncluded
 */

/**
 * Séparations de chapitre : déplacées dans le module feuille
 * `./chapterSeparation`, que le parser du web-editor peut charger sans tirer
 * l'éditeur classique. Réexportées ici pour les appelants historiques.
 */
export {
  getChapterSeparationContainerIds,
  filterChapterSeparationContainers,
} from './chapterSeparation';

// Déplacé dans ./titleRange (module feuille) pour être importable depuis
// structureLocks sans tirer la chaîne react-dnd. Ré-exporté ici pour les
// imports existants.
export { findTitleLevel1ContainerRange } from './titleRange';

// Container d'ancre d'une section liée : il reste dans `containers` mais n'est
// pas affiché (cf. InfinitePage), le contenu injecté le suit directement. Une
// ancre imbriquée fait partie du contenu de sa section parente, donc porte
// `from_linked_section`.
const isSectionLinkAnchor = (container, boxes) =>
  boxes.some(
    (b) =>
      b.columnId === container?.columnsIds?.[0] &&
      b.type === ItemTypes.SECTION_LINK
  );

// L'adder au-dessus de containers[index] est dans une section liée si le premier
// container visible au-dessus en fait aussi partie. Les ancres cachées sont
// sautées : sans ça, une section qui commence par une section imbriquée
// bloquait l'ajout juste au-dessus d'elle.
export const isInsideLinkedSection = (containers, boxes, index) => {
  if (!containers[index]?.from_linked_section) return false;
  for (let i = index - 1; i >= 0; i -= 1) {
    if (!containers[i].from_linked_section) return false;
    if (!isSectionLinkAnchor(containers[i], boxes)) return true;
  }
  return false;
};

// Premier index de la suite d'ancres cachées qui précède containers[index] :
// insérer au-dessus d'un contenu de section liée, c'est insérer avant ses ancres,
// sinon le nouveau container atterrit dans la section.
export const firstHiddenAnchorIndex = (containers, boxes, index) => {
  let first = index;
  while (first > 0 && isSectionLinkAnchor(containers[first - 1], boxes)) {
    first -= 1;
  }
  return first;
};
